import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MenuCard } from '../../../entities/menu-card';
import type { MenuItem } from '../../../shared/config/menuVoteData';
import { useResponsiveLength } from '../../../shared/lib/responsive';
import { Dot, Pagination, Root, Slide, Track, Viewport } from './MenuCarousel.styles';

export interface MenuCarouselProps {
  items: MenuItem[];
  /** 초기 활성 인덱스 */
  initialIndex?: number;
  onActiveChange?: (index: number) => void;
}

/** Figma 98:642/645/648 — wrapper 중심 기준 peek 좌표 (375px 화면) */
const ARCH = {
  rotateDeg: 15,
  /** 중앙 top 350 vs peek top 369 */
  sideYOffset: 19,
  /** left wrapper center 6.42px → Δ-181 / right wrapper center 373.42px → Δ+186 */
  sideXOffsetLeft: 181,
  sideXOffsetRight: 186,
} as const;

/** loop 캐러셀에서 active 기준 최단 거리 offset (-1 | 0 | 1 | …) */
function getWrappedOffset(index: number, activeIndex: number, length: number): number {
  if (length === 0) return 0;
  let diff = index - activeIndex;
  const half = length / 2;
  if (diff > half) diff -= length;
  if (diff < -half) diff += length;
  return diff;
}

function getSlideTransform(offset: number, len: (px: number) => string): string {
  if (offset === 0) {
    return 'translate(-50%, 0) translate3d(0, 0, 0) rotate(0deg)';
  }

  const x = offset < 0 ? -ARCH.sideXOffsetLeft : ARCH.sideXOffsetRight;
  const y = ARCH.sideYOffset;
  const rotate = offset * ARCH.rotateDeg;

  return `translate(-50%, 0) translate3d(${len(x)}, ${len(y)}, 0) rotate(${rotate}deg)`;
}

/**
 * 메뉴 카드 캐러셀 (feature)
 * - Figma 아치형(±15° 회전, 중앙 상단·좌우 하단) 3D peek 레이아웃.
 * - 좌우 스와이프/탭으로 전환, loop(끝 → 첫 카드).
 * - 페이지네이션 점과 활성 인덱스 동기화.
 */
export function MenuCarousel({ items, initialIndex = 0, onActiveChange }: MenuCarouselProps) {
  const len = useResponsiveLength();
  const [activeIndex, setActiveIndex] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(items.length - 1, 0)),
  );
  const pointerStart = useRef<{ x: number; y: number; slideIndex: number | null }>({
    x: 0,
    y: 0,
    slideIndex: null,
  });

  const wrapIndex = useCallback(
    (i: number) => {
      if (items.length === 0) return 0;
      return ((i % items.length) + items.length) % items.length;
    },
    [items.length],
  );

  const goTo = useCallback(
    (i: number) => {
      setActiveIndex(wrapIndex(i));
    },
    [wrapIndex],
  );

  useEffect(() => {
    onActiveChange?.(activeIndex);
  }, [activeIndex, onActiveChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(activeIndex - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(activeIndex + 1);
    }
  };

  const SWIPE_THRESHOLD = 40;
  const TAP_THRESHOLD = 10;

  const resolveSlideIndex = (target: EventTarget | null) => {
    const slide = (target as HTMLElement | null)?.closest('li[id^="menu-option-"]');
    if (!slide?.id) return null;
    const index = Number(slide.id.replace('menu-option-', ''));
    return Number.isNaN(index) ? null : index;
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLUListElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    pointerStart.current = {
      x: e.clientX,
      y: e.clientY,
      slideIndex: resolveSlideIndex(e.target),
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLUListElement>) => {
    const { x, y, slideIndex } = pointerStart.current;
    const dx = e.clientX - x;
    const dy = e.clientY - y;
    pointerStart.current = { x: 0, y: 0, slideIndex: null };

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      goTo(activeIndex + (dx < 0 ? 1 : -1));
      return;
    }

    if (Math.abs(dx) <= TAP_THRESHOLD && Math.abs(dy) <= TAP_THRESHOLD && slideIndex !== null) {
      goTo(slideIndex);
    }
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLUListElement>) => {
    pointerStart.current = { x: 0, y: 0, slideIndex: null };
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  return (
    <Root>
      <Viewport>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <Track
          role="listbox"
          aria-label="메뉴 카드 캐러셀"
          aria-activedescendant={`menu-option-${activeIndex}`}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
        >
          {items.map((item, i) => {
            const offset = getWrappedOffset(i, activeIndex, items.length);
            const isActive = offset === 0;
            const isVisible = Math.abs(offset) <= 1;

            return (
              <Slide
                key={item.id}
                id={`menu-option-${i}`}
                $active={isActive}
                $hidden={!isVisible}
                role="option"
                aria-selected={isActive}
                style={{
                  transform: getSlideTransform(offset, len),
                  zIndex: isActive ? 3 : 2,
                  opacity: isVisible ? 1 : 0,
                }}
              >
                <MenuCard item={item} active={isActive} />
              </Slide>
            );
          })}
        </Track>
      </Viewport>

      <Pagination aria-hidden="true">
        {items.map((item, i) => (
          <Dot key={item.id} $active={i === activeIndex} />
        ))}
      </Pagination>
    </Root>
  );
}
